import carouselImage1 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_1-min.jpg";
import carouselImage2 from "@/../../hypetrain/static/img/integration_flyout_carousel_image_2-min.jpg";
import { FirefliesLogo } from "@/components/ui/fireflies-logo";
import { IntegrationCard } from "@/components/ui/integration-card";
import { IntegrationFlyout } from "@/components/ui/integration-flyout";

import { FirefliesIntegrationModal } from "./FirefliesIntegrationModal";
import { type IntegrationConfig } from "./shared-utils";
import { useFirefliesIntegration } from "./useFirefliesIntegration";

export function FirefliesIntegration() {
  const {
    integrationName,
    setIntegrationName,
    token,
    setToken,
    isModalOpen,
    isFlyoutOpen,
    setIsFlyoutOpen,
    hasIntegrations,
    instances,
    statusLabel,
    integrationsLoading,
    createLoading,
    deleteLoading,
    openModal,
    closeModal,
    deleteIntegration,
  } = useFirefliesIntegration();

  const config: IntegrationConfig = {
    name: "Fireflies.ai",
    icon: <FirefliesLogo size={40} />,
    description:
      "Fireflies.ai automatically records, transcribes, and summarizes your calls from Zoom, Teams, and other platforms. Connect it to Exec to seamlessly import call recordings for AI-powered analysis and training.",
    carouselImages: [
      {
        src: carouselImage1,
        alt: "Automatically access all of your calls recordings",
      },
      {
        src: carouselImage2,
        alt: "Measure your team's performance in recorded meetings",
      },
    ],
    inputs: [
      {
        id: "integration-name",
        label: "Name",
        type: "text",
        placeholder: "e.g., Sales Team, Marketing",
        value: integrationName,
        onChange: setIntegrationName,
        required: true,
      },
      {
        id: "api-token",
        label: "API Access Token",
        type: "password",
        placeholder: "Enter your Fireflies API key",
        value: token,
        onChange: setToken,
        required: true,
      },
    ],
    setupDescription: (
      <div className="flex flex-col gap-3">
        <p className="text-base font-normal leading-snug text-gray-800 m-0">
          Get an access token from your Fireflies workspace
        </p>
        <div className="text-base font-normal leading-relaxed">
          <ul className="list-disc ml-5 space-y-1">
            <li>
              Log in to your account at{" "}
              <a
                href="https://app.fireflies.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-600"
              >
                app.fireflies.ai
              </a>
            </li>
            <li>
              Navigate to the{" "}
              <a
                href="https://app.fireflies.ai/settings"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-600"
              >
                Settings
              </a>{" "}
              section
            </li>
            <li>Click on "Developer Settings"</li>
            <li>Copy your API key and input below</li>
          </ul>
        </div>
      </div>
    ),
  };

  return (
    <>
      <IntegrationFlyout
        integrationName={config.name}
        icon={config.icon}
        status={hasIntegrations ? "connected" : "disconnected"}
        statusLabel={statusLabel}
        description={config.description}
        carouselImages={config.carouselImages}
        instances={instances}
        open={isFlyoutOpen}
        onOpenChange={setIsFlyoutOpen}
        onConnect={openModal}
        onDisconnectInstance={deleteIntegration}
        loading={createLoading || deleteLoading}
      >
        <IntegrationCard
          name={config.name}
          icon={config.icon}
          status={hasIntegrations ? "connected" : "disconnected"}
          statusLabel={statusLabel}
          onConnect={hasIntegrations ? undefined : () => setIsFlyoutOpen(true)}
          onManage={hasIntegrations ? () => setIsFlyoutOpen(true) : undefined}
          loading={integrationsLoading || createLoading}
        />
      </IntegrationFlyout>

      <FirefliesIntegrationModal
        open={isModalOpen}
        onOpenChange={(open) => !open && closeModal()}
      />
    </>
  );
}
